import React, { useEffect } from "react";
import { Box, Flex, Icon, Stack, Text } from "@chakra-ui/react";
import { FiKey, FiHash, FiShield, FiSlash, FiLink, FiCheckCircle, FiLock, FiUnlock, FiSearch } from "react-icons/fi";
import { InfoPage, InfoBody, InfoSectionTitle, NumberedSteps, FaqList } from "../infoShared";

// Pełna ścieżka tajnego głosu — wytłumaczona bez matematyki.
const STEPS = [
  {
    icon: FiKey,
    title: "Twój sekret powstaje u Ciebie",
    desc: "Na Twoim urządzeniu tworzy się tajny klucz. Nie wysyłamy go nigdzie — nie ma go na naszym serwerze ani w żadnej bazie. To on sprawia, że tylko Ty możesz oddać swój głos."
  },
  {
    icon: FiHash,
    title: "Trafiasz na listę uprawnionych — anonimowo",
    desc: "Na publiczną listę uprawnionych trafia nie Twoje nazwisko, tylko jego kryptograficzny odcisk. Widać, że ktoś uprawniony może zagłosować — nie widać, kto."
  },
  {
    icon: FiShield,
    title: "Twoje urządzenie tworzy dowód",
    desc: "Przy oddaniu głosu urządzenie buduje dowód z wiedzą zerową: potwierdza, że jesteś jednym z uprawnionych i oddajesz ten głos — nie zdradzając, którym z uprawnionych jesteś."
  },
  {
    icon: FiSlash,
    title: "Powstaje jednorazowy znacznik „głosowałem”",
    desc: "Z Twojego sekretu liczy się jednorazowy znacznik. Pilnuje, żeby nikt nie zagłosował dwa razy — a mimo to nie da się go powiązać z Tobą."
  },
  {
    icon: FiLink,
    title: "Głos trafia na blockchain",
    desc: "Głos i dowód zapisują się na łańcuchu: publicznie, trwale i bez możliwości podmiany. Nie ma przy nich Twojej tożsamości."
  },
  {
    icon: FiCheckCircle,
    title: "Każdy może sprawdzić wynik",
    desc: "Wynik liczy się z samych dowodów. Każdy — Ty też — może go przeliczyć i potwierdzić, że jest uczciwy. Nie musisz nikomu wierzyć na słowo."
  }
];

const WHY = [
  {
    icon: FiLock,
    title: "Tajność i dowód naraz",
    desc: "Dotąd wynik dało się albo sprawdzić (ale jawny), albo utajnić (ale na czyjeś słowo). Tu masz jedno i drugie: pełną tajność wyboru i pełną weryfikowalność wyniku."
  },
  {
    icon: FiUnlock,
    title: "Zero zaufania",
    desc: "Nie musisz ufać nam, serwerowi ani osobom liczącym głosy. Uczciwość gwarantuje matematyka, a nie obietnica."
  },
  {
    icon: FiSearch,
    title: "Odporne nawet na wyciek",
    desc: "Gdyby ktoś przejął całą naszą bazę i serwery, dalej nie odtworzy, jak ktokolwiek zagłosował. Tożsamości i wyboru nigdzie nie ma razem."
  }
];

const FAQS = [
  {
    q: "Czy naprawdę nikt nie wie, jak zagłosowałem?",
    a: "Tak. Głos zapisuje się bez Twojej tożsamości, a dowód potwierdza jego ważność, nie ujawniając wyboru. Nawet my, prowadząc system, nie mamy jak powiązać Cię z konkretnym głosem."
  },
  {
    q: "To skąd wiadomo, że nie zagłosowałem dwa razy?",
    a: "Z Twojego sekretu powstaje jednorazowy znacznik. Łańcuch odrzuci drugi głos z tym samym znacznikiem, a samego znacznika nie da się cofnąć do Twojej osoby."
  },
  {
    q: "Co jeśli ktoś ukradnie bazę albo serwer?",
    a: "Nadal nie odtworzy, jak głosowałeś. Powiązanie tożsamości z wyborem nie istnieje w żadnej bazie — nie ma czego wykraść."
  },
  {
    q: "Czy muszę znać się na kryptografii, żeby zaufać?",
    a: "Nie. Na tym polega różnica: nie prosimy, żebyś nam wierzył. Dowody są publiczne i każdy może je niezależnie sprawdzić."
  },
  {
    q: "Czym to się różni od zwykłego głosowania online?",
    a: "Zwykłe systemy proszą o zaufanie: „obiecujemy, że nie podglądamy i liczymy uczciwie”. Tu zaufanie zastępuje dowód matematyczny, a wynik kotwiczy się na blockchainie."
  }
];

const WhyCard: React.FC<{ icon: React.ElementType; title: string; desc: string }> = ({ icon, title, desc }) => (
  <Box bg="white" borderRadius="18px" p={{ base: "20px", md: "24px" }} boxShadow="0 4px 18px rgba(67,0,152,0.06)" border="1px solid rgba(67,0,152,0.06)">
    <Flex w="42px" h="42px" borderRadius="12px" bg="rgba(67,0,152,0.08)" align="center" justify="center" mb="14px">
      <Icon as={icon} color="primary" boxSize="20px" />
    </Flex>
    <Text fontWeight="800" color="primary" fontSize={{ base: "17px", md: "18px" }} mb="6px">
      {title}
    </Text>
    <Text color="gray.600" fontSize={{ base: "14px", md: "15px" }} lineHeight="1.6">
      {desc}
    </Text>
  </Box>
);

export const ZeroKnowledgeProofView: React.FC = () => {
  useEffect(() => {
    document.title = "Tajne głosowanie i dowód z wiedzą zerową (ZKP)";
  }, []);

  return (
    <InfoPage
      eyebrow="Jak to działa"
      title="Tajne głosowanie i dowód z wiedzą zerową"
      subtitle="Możesz udowodnić, że Twój głos jest ważny i policzony — a mimo to nikt, łącznie z nami, nie dowie się, jak zagłosowałeś."
    >
      <InfoBody narrow>
        <InfoSectionTitle>O co chodzi</InfoSectionTitle>
        <Stack spacing="18px" color="gray.700" fontSize={{ base: "16px", md: "17px" }} lineHeight="1.7">
          <Text>
            Tajne głosowanie zwykle opiera się na zaufaniu — do ludzi, którzy liczą głosy, i do systemu, który trzyma
            dane. Trzeba wierzyć, że nikt nie podejrzał karty i nie powiązał jej z nazwiskiem.
          </Text>
          <Text>
            Dowód z wiedzą zerową (ZKP) usuwa to „trzeba wierzyć”. Pozwala udowodnić, że coś jest prawdą, nie ujawniając
            samej rzeczy. To jak potwierdzić, że jesteś pełnoletni, nie pokazując daty urodzenia — druga strona dostaje
            pewność, a nie dane.
          </Text>
        </Stack>

        <Box mt={{ base: "26px", md: "32px" }} bgGradient="linear(135deg, #430098 0%, #6a1fc2 100%)" color="white" borderRadius="20px" p={{ base: "22px", md: "30px" }}>
          <Text fontSize={{ base: "18px", md: "22px" }} fontWeight="800" lineHeight="1.45">
            Twoje nazwisko i Twój wybór nigdy nie spotykają się w jednym miejscu. Nigdzie. A wynik i tak da się sprawdzić
            co do jednego głosu.
          </Text>
        </Box>

        <Box mt={{ base: "44px", md: "60px" }}>
          <InfoSectionTitle>Jak Twój głos zostaje anonimowy</InfoSectionTitle>
          <NumberedSteps steps={STEPS} />
        </Box>

        <Box mt={{ base: "44px", md: "60px" }}>
          <InfoSectionTitle>Dlaczego to jest przełom</InfoSectionTitle>
          <Text color="gray.700" fontSize={{ base: "16px", md: "17px" }} lineHeight="1.7" mb={{ base: "24px", md: "30px" }}>
            Tajność, weryfikowalność i brak zaufania długo się wykluczały — można było mieć najwyżej dwie z tych rzeczy
            naraz. Tutaj zachodzą wszystkie trzy jednocześnie.
          </Text>
          <Stack spacing="16px">
            {WHY.map(w => (
              <WhyCard key={w.title} icon={w.icon} title={w.title} desc={w.desc} />
            ))}
          </Stack>
        </Box>

        <Box mt={{ base: "44px", md: "60px" }}>
          <InfoSectionTitle>Częste pytania</InfoSectionTitle>
          <FaqList items={FAQS} />
        </Box>
      </InfoBody>
    </InfoPage>
  );
};
